'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { Console, Flag, TicketStar } from '@shared/icons';
import { ProductCommunications } from '@shared/icons/system';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { MultistepForm } from '../../../form-controls/MultistepForm/MultistepForm';
import type { ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';
import { toast } from '../../../Toast/Toast';
import { useStepTracking } from '../../hooks/useStepTracking';
import { HiddenTrigger, MyAccountSheet } from '../../MyAccountSheet/MyAccountSheet';
import { buildContactPreferencesPayload, resolveEffectiveChannels, type ContactPreferencesPayload } from '../utils/contactPreferencesPayload';
import { ContactPreferencesForm } from './ContactPreferencesForm';

type ProductPreferenceValue = 'lottery' | 'casino' | 'sportsbook';

type ContactPreferencesEditSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactPreferences?: { verticals: string[]; channels: string[] };
  productSelection?: ProductPreferenceValue[];
  onProductSelectionChange?: (selected: ProductPreferenceValue[]) => void;
};

function ContactPreferencesEditSheet({
  open,
  onOpenChange,
  contactPreferences = { verticals: [], channels: [] },
  productSelection,
  onProductSelectionChange,
}: ContactPreferencesEditSheetProps) {
  const t = useTranslations('my_account');
  const queryClient = useQueryClient();
  const debug = createDebug({ isEnabled: false });
  const options: ToggleOption[] = [
    { value: 'lottery', label: t('settings.contact_preferences.products.labels.lotteries'), icon: <TicketStar />, trackingProps: { 'data-track-name': 'Lotteries' } },
    { value: 'casino', label: t('settings.contact_preferences.products.labels.casino_live'), icon: <Console />, trackingProps: { 'data-track-name': 'Casino & Live Casino' } },
    { value: 'sportsbook', label: t('settings.contact_preferences.products.labels.sportsbook'), icon: <Flag />, trackingProps: { 'data-track-name': 'Sportsbook' } },
  ];

  const { mutateAsync: savePreferences } = useApiMutation<{ success: boolean }, ContactPreferencesPayload>(API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.PUT, 'PUT', {
    requestOptions: {
      credentials: true,
    },
    mutationOptions: {
      onError: (error: Error) => {
        debug.forceError('Error updating contact preferences:', error);
      },
      onSettled: () => {
        const queryKey = ['api', API_ROUTES.MY_ACCOUNT.CONTACT_PREFERENCES.GET];
        queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  const handleSave = async (selected: ('lottery' | 'casino' | 'sportsbook')[]) => {
    try {
      const effectiveChannels = resolveEffectiveChannels(selected, contactPreferences.channels);
      await savePreferences(buildContactPreferencesPayload(selected, effectiveChannels));
      onOpenChange(false);
    } catch (error) {
      debug.forceError('Error updating contact preferences:', error);
      toast(t('settings.contact_preferences.save_error'), { type: 'error' });
    }
  };

  const steps = [
    {
      id: 'contact-preferences',
      component: (
        <div className="w-full flex flex-col flex-1 gap-8 p-6 md:p-8 md:max-w-2xl overflow-y-auto">
          <div className="flex flex-col gap-2">
            {/* Step indicator */}
            <ProductCommunications size="extra-extra-large" data-testid="product-communications-icon" />

            {/* Content */}
            <div className="flex flex-col gap-5 items-start justify-center">
              <div className="flex flex-col gap-2 w-full">
                <h4>{t('settings.contact_preferences.products.title')}</h4>
              </div>
            </div>
          </div>

          <ContactPreferencesForm
            initialSelected={contactPreferences.verticals as ProductPreferenceValue[]}
            selected={productSelection}
            onSelectedChange={onProductSelectionChange}
            options={options}
            onSubmit={handleSave}
          />
        </div>
      ),
      schema: {} as unknown as import('zod').ZodType,
      'data-track-container': 'Product Preference Edit',
    },
  ];

  // Use step tracking hook
  const { currentTrackContainer } = useStepTracking(steps);

  if (!open) return null;

  return (
    <MyAccountSheet
      trigger={<HiddenTrigger label={t('settings.contact_preferences.product_preference')} />}
      title={t('settings.contact_preferences.product_preference')}
      open={open}
      onOpenChange={onOpenChange}
      data-track-container={currentTrackContainer}
    >
      <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} className="flex flex-col h-full" />
    </MyAccountSheet>
  );
}

export { ContactPreferencesEditSheet };
